import { PrimaryButton } from "components/Button/style";
import { MaisFiltros } from "components/ButtonModal";
import { CustomInput } from "components/CustomInput";
import { CustomInputModal } from "components/CustomInputModal";
import { CustomDatePicker } from "components/DatePicker";
import { DropdownSelect } from "components/DropdownSelect";
import { TooltipBootstrap } from "components/TooltipBootstrap";
import { useMemo } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FILTROS_LOTE_VAZIOS } from "../../constants/filtrosLote";
import { OPCOES_PRENSA } from "../../constants/opcoesSolicitarAutorizacao";
import {
  opcoesStatusAutorizacaoLote,
  opcoesStatusProducaoLote,
} from "../../constants/status";
import { useUsuariosSolicitacaoPlaca } from "../../hooks/useUsuariosSolicitacaoPlaca";
import { IFiltrosLote } from "../../interfaces/IFiltrosLote";
import { codigoOuNulo } from "../../utils/codigoOuNulo";
import {
  BotoesContainer,
  CampoMaisFiltros,
  Container,
  IconeInfo,
  MaisFiltrosContainer,
  MaisFiltrosDropdownWrapper,
  Title,
} from "../Filtros/style";

type CampoCodigo =
  | "prensa"
  | "statusAutorizacao"
  | "statusProducao"
  | "idUsuario";

interface IProps {
  onBuscar: (filtros: IFiltrosLote) => void;
  valoresIniciais?: IFiltrosLote;
}

export function FiltrosLotes({ onBuscar, valoresIniciais }: IProps) {
  const { control, handleSubmit, reset, setValue } = useForm<IFiltrosLote>({
    defaultValues: valoresIniciais ?? FILTROS_LOTE_VAZIOS,
  });
  const prensa = useWatch({ control, name: "prensa" });
  const statusAutorizacao = useWatch({ control, name: "statusAutorizacao" });
  const statusProducao = useWatch({ control, name: "statusProducao" });
  const idUsuario = useWatch({ control, name: "idUsuario" });
  const periodo = useWatch({ control, name: "periodoCriacao" });
  const [dataInicio, dataFim] = periodo ?? [];
  const { usuariosOptions } = useUsuariosSolicitacaoPlaca();

  // Estáveis: o DropdownSelect chama onSelect num efeito que depende dele.
  const selecionar = useMemo(() => {
    const em = (campo: CampoCodigo) => (valor: string | number | null) =>
      setValue(campo, codigoOuNulo(valor));

    return {
      prensa: em("prensa"),
      statusAutorizacao: em("statusAutorizacao"),
      statusProducao: em("statusProducao"),
      idUsuario: em("idUsuario"),
    };
  }, [setValue]);

  function handleLimparMaisFiltros() {
    setValue("idUsuario", null);
    setValue("periodoCriacao", null);
    setValue("placa", "");
  }

  function handleLimpar() {
    reset(FILTROS_LOTE_VAZIOS);
    onBuscar(FILTROS_LOTE_VAZIOS);
  }

  return (
    <Container>
      <Title>Filtros</Title>
      <hr />
      <Form noValidate onSubmit={handleSubmit((filtros) => onBuscar(filtros))}>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>
                PDI{" "}
                <TooltipBootstrap
                  childrenToolTip={
                    <span>
                      Serão considerados todos os PDIs que contenham a palavra
                      informada
                    </span>
                  }
                >
                  <span>
                    <IconeInfo />
                  </span>
                </TooltipBootstrap>
              </Form.Label>
              <Controller
                control={control}
                name="pdi"
                render={({ field }) => (
                  <CustomInput
                    type="text"
                    placeholder="Busque por PDI"
                    style={{ height: "2.2rem" }}
                    {...field}
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Lote</Form.Label>
              <Controller
                control={control}
                name="idLote"
                render={({ field: { value, onChange } }) => (
                  <CustomInputModal
                    label="Filtrar em massa"
                    value={value}
                    placeholder="Busque pelo lote"
                    onConfirm={onChange}
                    validar={false}
                    style={{ width: "100%" }}
                    placeholderModal="Digite ou cole os lotes e tecle ENTER"
                    labelItem="Lote"
                  />
                )}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Prensa</Form.Label>
              <DropdownSelect
                options={OPCOES_PRENSA}
                selectedValue={prensa}
                onSelect={selecionar.prensa}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Status autorização</Form.Label>
              <DropdownSelect
                options={opcoesStatusAutorizacaoLote}
                selectedValue={statusAutorizacao}
                onSelect={selecionar.statusAutorizacao}
                permitirZero
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Status produção</Form.Label>
              <DropdownSelect
                options={opcoesStatusProducaoLote}
                selectedValue={statusProducao}
                onSelect={selecionar.statusProducao}
                permitirZero
              />
            </Form.Group>
          </Col>
          <Col md="auto">
            <BotoesContainer>
              <PrimaryButton
                type="button"
                outline
                variante="primary"
                onClick={handleLimpar}
              >
                Limpar
              </PrimaryButton>
              <PrimaryButton type="submit">Buscar</PrimaryButton>
            </BotoesContainer>
          </Col>
          <Col style={{ paddingTop: "5px" }}>
            <MaisFiltrosContainer>
              <Form.Group>
                <MaisFiltrosDropdownWrapper>
                  <MaisFiltros
                    limparTudoOnClick={handleLimparMaisFiltros}
                    cancelarOnClick={handleLimparMaisFiltros}
                    handleClickAplicarFiltros={() => {}}
                    widthModal="lg"
                    submit
                  >
                    <CampoMaisFiltros>
                      <Form.Group>
                        <Form.Label>Usuário responsável</Form.Label>
                        <DropdownSelect
                          options={usuariosOptions}
                          selectedValue={idUsuario}
                          onSelect={selecionar.idUsuario}
                          searchable
                        />
                      </Form.Group>
                    </CampoMaisFiltros>

                    <CampoMaisFiltros>
                      <Form.Group>
                        <Form.Label>Período de criação</Form.Label>
                        <Controller
                          control={control}
                          name="periodoCriacao"
                          render={({ field: { onChange } }) => (
                            <CustomDatePicker
                              placeholder="Selecione o período"
                              rangeSelect
                              startDate={dataInicio ?? undefined}
                              endDate={dataFim ?? undefined}
                              onChange={(datas) => onChange(datas)}
                            />
                          )}
                        />
                      </Form.Group>
                    </CampoMaisFiltros>

                    <CampoMaisFiltros>
                      <Form.Group>
                        <Form.Label>Placa</Form.Label>
                        <Controller
                          control={control}
                          name="placa"
                          render={({ field }) => (
                            <CustomInput
                              type="text"
                              placeholder="Busque pela placa"
                              style={{ height: "2.2rem" }}
                              {...field}
                            />
                          )}
                        />
                      </Form.Group>
                    </CampoMaisFiltros>
                  </MaisFiltros>
                </MaisFiltrosDropdownWrapper>
              </Form.Group>
            </MaisFiltrosContainer>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}
